/* GrcMatrix — Risques & Contrôles : projection interactive de l'ontologie canonique.
 * UPLOAD → UNDERSTAND → PROPOSE → VALIDATION HUMAINE → MATERIALIZE → REPRESENT → MONITOR.
 * Zéro configuration manuelle du dashboard. Provenance vérifiable partout. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  ShieldAlert, UploadCloud, Loader2, CheckCircle2, ChevronRight, MessageCircle,
  AlertTriangle, Clock, TrendingDown, TrendingUp, Minus, FileSpreadsheet, Filter, FileSearch,
} from "lucide-react";
import ContextStack from "@/components/ContextStack";
import { useTranslation } from "react-i18next";

const LEVEL_COLOR = { Rouge: "#E24B4A", Orange: "#EF9F27", Vert: "#1D9E75", inconnu: "#71717a" };
const EPI = { OBSERVED: "#1D9E75", DERIVED: "#378ADD", INFERRED: "#EF9F27" };

function EpiBadge({ kind }) {
  const { t } = useTranslation();
  const label = { OBSERVED: t("saas.axp2.epi_observed"), DERIVED: t("saas.axp2.epi_derived"), INFERRED: t("saas.axp2.epi_inferred") }[kind] || kind;
  return (
    <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 rounded border"
      style={{ color: EPI[kind], borderColor: `${EPI[kind]}4d`, background: `${EPI[kind]}14` }}>
      {label}
    </span>
  );
}

function Provenance({ p }) {
  if (!p) return null;
  return (
    <span className="text-[9px] text-zinc-500 font-mono" title={p.file}>
      [{p.sheet}!L{p.row}]
    </span>
  );
}

function TrendIcon({ t }) {
  if (!t || t.direction === "insuffisant") return <Minus className="w-3 h-3 text-zinc-600" />;
  if (t.direction === "degradation") return <TrendingDown className="w-3 h-3 text-[#E24B4A]" />;
  if (t.direction === "amelioration") return <TrendingUp className="w-3 h-3 text-[#1D9E75]" />;
  return <Minus className="w-3 h-3 text-zinc-500" />;
}

export default function GrcMatrix() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [materializing, setMaterializing] = useState(false);
  const [stack, setStack] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/workspace/grc/ingest/matrix")
    .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const analyze = async (f) => {
    if (!f) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/workspace/grc/ingest/analyze", fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setProposal(r.data);
    } catch (e) { notify.error(fmtError(e, t("saas.axp2.grc_an_fail"))); }
    setAnalyzing(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const materialize = async () => {
    setMaterializing(true);
    try {
      const r = await api.post(`/workspace/grc/ingest/proposals/${proposal.proposal_id}/materialize`);
      notify.success(`Matérialisé : ${r.data.created?.risks ?? 0} risques, ${r.data.created?.controls ?? 0} contrôles, ${r.data.created?.executions ?? 0} exécutions`);
      setProposal(null);
      await load();
    } catch (e) { notify.error(fmtError(e, t("saas.axp2.grc_mat_fail"))); }
    setMaterializing(false);
  };

  const risks = data?.risks || [];
  const filtered = useMemo(() => risks.filter((r) => {
    if (filter === "uncovered") return !r.covered;
    if (filter === "overdue") return r.controls.some((c) => c.days_overdue);
    if (filter === "below") return r.controls.some((c) => c.below_threshold);
    if (["Rouge", "Orange", "Vert"].includes(filter)) return r.level === filter;
    return true;
  }), [risks, filter]);

  const cell = (p, i) => risks.filter((r) => r.probability === p && r.impact === i);
  const s = data?.summary;

  /* ─── Navigation contextuelle générique : Objet → Relation → Objet → Preuve ── */
  const push = (panel) => setStack((st) => [...st, panel]);

  const execPanel = (x, c) => ({
    id: `exec-${c.code}-${x.date}`, crumb: `${t("saas.axp2.grc_exec_of")} ${x.date || "—"}`,
    title: `${t("saas.axp2.grc_exec_of")} ${x.date || "—"}`,
    subtitle: `${c.code} — ${c.nom}`,
    badge: <div className="flex items-center gap-2"><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded font-bold ${x.compliant ? "bg-[#1D9E75] text-black" : "bg-[#E24B4A] text-white"}`}>{x.compliant ? t("saas.axp2.grc_compliant") : t("saas.axp2.grc_noncompliant")}</span><EpiBadge kind="OBSERVED" /></div>,
    render: () => (
      <div className="space-y-3 text-sm" data-testid="grc-exec-panel">
        <div className="grid grid-cols-2 gap-3">
          <div className="noam-card p-3"><div className="text-[9px] uppercase tracking-widest text-zinc-500">{t("saas.axp2.grc_result")}</div>
            <div className={`text-xl font-bold ${x.result_pct != null && c.threshold_pct != null && x.result_pct < c.threshold_pct ? "text-[#E24B4A]" : "text-white"}`}>{x.result_pct ?? "—"}%</div>
            <div className="text-[10px] text-zinc-500">{t("saas.axp2.grc_target")} {c.threshold_pct ?? "—"}% {x.result_pct != null && c.threshold_pct != null && <>· {t("saas.axp2.grc_gap")} {Math.round((x.result_pct - c.threshold_pct) * 100) / 100} <EpiBadge kind="DERIVED" /></>}</div></div>
          <div className="noam-card p-3"><div className="text-[9px] uppercase tracking-widest text-zinc-500">{t("saas.axp2.grc_verif")}</div>
            <div className="text-sm text-white mt-1">{x.verified_by || "—"}</div>
            <div className="text-[10px] text-zinc-500">{t("saas.axp2.grc_on")} {x.verified_date || "—"}</div></div>
        </div>
        {x.comment && <div className="noam-card p-3 text-xs text-zinc-300">« {x.comment} » <EpiBadge kind="OBSERVED" /></div>}
        <div className="rounded-lg border border-[#378ADD]/30 bg-[#378ADD]/10 p-3" data-testid="grc-exec-provenance">
          <div className="text-[9px] uppercase tracking-widest text-[#378ADD] mb-1 flex items-center gap-1.5"><FileSearch className="w-3 h-3" /> {t("saas.axp2.grc_source_data")}</div>
          {x.provenance ? (
            <div className="text-xs text-zinc-200 font-mono">{x.provenance.file}<br />Feuille « {x.provenance.sheet} » · Ligne {x.provenance.row}</div>
          ) : <div className="text-xs text-zinc-500">{t("saas.axp2.grc_no_prov")}</div>}
        </div>
      </div>
    ),
  });

  const controlPanel = (c, r) => ({
    id: `ctrl-${c.code}`, crumb: c.code || t("saas.axp2.grc_ctrl"),
    title: `${c.code} — ${c.nom}`,
    subtitle: `${t("saas.axp2.grc_covers")} ${r.code} — ${r.title}`,
    badge: <div className="flex items-center gap-2"><TrendIcon t={c.trend} /><Provenance p={c.provenance} /><EpiBadge kind="OBSERVED" /></div>,
    render: (pushNext) => (
      <div className="space-y-4" data-testid={`grc-control-panel-${c.code}`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-[11px] text-zinc-400">
          <div>{t("saas.axp2.grc_resp")} : <span className="text-zinc-200">{c.responsible || "—"}</span></div>
          <div>{t("saas.axp2.grc_verifier")} : <span className="text-zinc-200">{c.verifier || "—"}</span></div>
          <div>{t("saas.axp2.grc_level")} : <span className="text-zinc-200">{c.line_level || "—"}</span> · {c.periodicity || "—"}</div>
          <div>{t("saas.axp2.grc_due")} : <span className={c.days_overdue ? "text-[#E24B4A] font-medium" : "text-zinc-200"}>
            {c.next_due || "—"}{c.days_overdue ? ` (${t("saas.axp2.grc_late", { n: c.days_overdue })})` : c.days_to_due != null ? ` (${t("saas.axp2.grc_in_days", { n: c.days_to_due })})` : ""}</span> <EpiBadge kind="DERIVED" /></div>
          <div>{t("saas.axp2.grc_result")} : <span className={c.below_threshold ? "text-[#E24B4A] font-medium" : "text-zinc-200"}>{c.result_pct ?? "—"}%</span> / {t("saas.axp2.grc_target")} {c.threshold_pct ?? "—"}%</div>
          <div>{t("saas.axp2.grc_trend")} : <span className="text-zinc-200">{c.trend?.direction || "—"}{c.trend?.delta != null ? ` (${c.trend.delta > 0 ? "+" : ""}${c.trend.delta} pts)` : ""}</span> <EpiBadge kind="DERIVED" /></div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">{t("saas.axp2.grc_hist", { n: c.executions?.length || 0 })} <EpiBadge kind="OBSERVED" /> · {t("saas.axp2.grc_click_proof")}</div>
          {(c.executions || []).length === 0 ? (
            <div className="text-xs text-zinc-500 italic">{t("saas.axp2.grc_no_exec")}</div>
          ) : c.executions.map((x, i) => (
            <button key={i} onClick={() => pushNext(execPanel(x, c))}
              data-testid={`grc-exec-row-${c.code}-${i}`}
              className="w-full flex items-center gap-2 text-[11px] px-2.5 py-2 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/20 transition-colors mb-1 text-left">
              <span className="text-zinc-500 font-mono w-20 shrink-0">{x.date || "—"}</span>
              <span className={`font-mono w-12 shrink-0 ${x.result_pct != null && c.threshold_pct != null && x.result_pct < c.threshold_pct ? "text-[#E24B4A]" : "text-zinc-200"}`}>{x.result_pct ?? "—"}%</span>
              <span className={`w-4 shrink-0 ${x.compliant ? "text-[#1D9E75]" : "text-[#E24B4A]"}`}>{x.compliant ? "✓" : "✗"}</span>
              <span className="text-zinc-400 flex-1 truncate">{x.comment || ""}</span>
              <Provenance p={x.provenance} />
              <ChevronRight className="w-3 h-3 text-zinc-600 shrink-0" />
            </button>
          ))}
        </div>
      </div>
    ),
  });

  const riskPanel = (r) => ({
    id: `risk-${r.code || r.id}`, crumb: r.code || t("saas.axp2.grc_risk"),
    title: `${r.code} — ${r.title}`,
    subtitle: `${r.process}${r.subprocess ? ` · ${r.subprocess}` : ""} · ${t("saas.axp2.grc_owner")} : ${r.owner || "—"} · ${t("saas.axp2.grc_trend")} : ${r.trend || "—"}`,
    badge: (
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-black font-bold" style={{ background: LEVEL_COLOR[r.level] }}>
          {t(`saas.axp3.lvl_${r.level}`, { defaultValue: r.level })} · {r.criticality}
        </span>
        <EpiBadge kind={r.level_epistemic || "DERIVED"} />
        <Provenance p={r.provenance} />
      </div>
    ),
    render: (pushNext) => (
      <div className="space-y-4" data-testid={`grc-risk-panel-${r.code}`}>
        <button
          onClick={() => navigate(`/app/intelligence?intent=${encodeURIComponent(`Parle-moi du risque ${r.code} « ${r.title} » : contrôles, résultats, évolution et ce qui mérite mon attention.`)}`)}
          className="btn-secondary text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5" data-testid="grc-talk-to-noam">
          <MessageCircle className="w-3.5 h-3.5" /> {t("saas.axp.grc_talk")}
        </button>
        {r.controls.length === 0 ? (
          <div className="rounded-lg border border-[#E24B4A]/30 bg-[#E24B4A]/10 px-4 py-3 text-xs text-[#E24B4A]" data-testid="grc-drilldown-uncovered">
            <AlertTriangle className="w-4 h-4 inline mr-1.5" /> {t("saas.axp2.grc_no_ctrl")} <EpiBadge kind="DERIVED" />
          </div>
        ) : (
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">{t("saas.axp2.grc_ctrls_click", { n: r.controls.length })}</div>
            {r.controls.map((c) => (
              <button key={c.id} onClick={() => pushNext(controlPanel(c, r))}
                data-testid={`grc-control-${c.code}`}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/20 transition-colors mb-1.5 text-left">
                <span className="text-[10px] font-mono text-zinc-500 shrink-0">{c.code}</span>
                <span className="text-xs text-zinc-200 flex-1 truncate">{c.nom}</span>
                {c.days_overdue ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30 shrink-0 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {c.days_overdue} j</span> : null}
                <span className={`text-[10px] font-mono shrink-0 ${c.below_threshold ? "text-[#E24B4A]" : "text-zinc-400"}`}>{c.result_pct ?? "—"}/{c.threshold_pct ?? "—"}%</span>
                <TrendIcon t={c.trend} />
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    ),
  });

  const openRisk = (r) => setStack([riskPanel(r)]);

  return (
    <div className="space-y-5" data-testid="grc-matrix-root">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="noam-overline inline-flex items-center gap-2">
            <ShieldAlert className="w-3 h-3 text-[#E24B4A]" /> {t("saas.axp.grc_overline")}
          </div>
          <h2 className="noam-display text-xl sm:text-2xl font-bold tracking-tight">{t("saas.axp.grc_title")}</h2>
          <p className="text-zinc-400 text-sm mt-1">{t("saas.axp.grc_sub")}</p>
        </div>
        <div>
          <input ref={fileRef} type="file" className="hidden" accept=".xlsx,.xlsm"
            onChange={(e) => analyze(e.target.files?.[0])} data-testid="grc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={analyzing}
            className="btn-primary text-xs px-3 py-2 rounded-lg flex items-center gap-2" data-testid="grc-upload-btn">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {analyzing ? t("saas.axp.grc_analyzing") : t("saas.axp.grc_upload")}
          </button>
        </div>
      </div>

      {/* ─── PROPOSE → VALIDATION HUMAINE ─────────────────────────────── */}
      {proposal && (
        <div className="noam-card p-5 border-[#378ADD]/40" data-testid="grc-proposal">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet className="w-4 h-4 text-[#378ADD]" />
            <div className="text-sm font-medium">{t("saas.axp.grc_understood", { file: proposal.filename })}</div>
            <span className="text-[9px] text-zinc-500 font-mono uppercase">mapping : {proposal.mapping_source}</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            {["risks", "controls", "executions"].map((k) => (
              <div key={k} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
                <div className="text-lg font-bold text-white">{proposal.summary?.counts?.[k] ?? 0} <EpiBadge kind="DERIVED" /></div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{{ risks: t("saas.axp.grc_risks"), controls: t("saas.axp.grc_controls"), executions: t("saas.axp.grc_execs") }[k]}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-zinc-300 space-y-1.5 mb-3">
            {(proposal.summary?.uncovered_risks || []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="grc-proposal-uncovered">
                <AlertTriangle className="w-3.5 h-3.5 text-[#E24B4A]" />
                <span className="text-[#E24B4A] font-medium">{t("saas.axp.grc_uncovered_lbl")}</span>
                {proposal.summary.uncovered_risks.map((u) => (
                  <span key={u.id} className="px-1.5 py-0.5 rounded bg-[#E24B4A]/10 border border-[#E24B4A]/30 text-[11px]">
                    {u.id} — {u.label} <Provenance p={u.provenance} />
                  </span>
                ))}
                <EpiBadge kind="DERIVED" />
              </div>
            )}
            {(proposal.summary?.overdue_controls || []).map((c) => (
              <div key={c.control_id} className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-[#EF9F27]" />
                {t("saas.axp.grc_overdue_lbl")} {c.control_id} — {c.label} ({c.days_overdue} j) <Provenance p={c.provenance} /> <EpiBadge kind="DERIVED" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={materialize} disabled={materializing}
              className="btn-primary text-xs px-3 py-2 rounded-lg flex items-center gap-2" data-testid="grc-materialize-btn">
              {materializing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {t("saas.axp.grc_materialize")}
            </button>
            <button onClick={() => setProposal(null)} className="btn-ghost text-xs" data-testid="grc-proposal-cancel">{t("saas.axp.grc_cancel")}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="noam-card p-10 text-center text-zinc-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> {t("saas.axp.grc_loading")}
        </div>
      ) : risks.length === 0 ? (
        <div className="noam-card p-10 text-center" data-testid="grc-empty">
          <ShieldAlert className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-zinc-400">{t("saas.axp.grc_empty")}</p>
        </div>
      ) : (
        <>
          {/* ─── Signaux déterministes ───────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button onClick={() => setFilter(filter === "uncovered" ? "all" : "uncovered")}
              className={`noam-card px-4 py-3 text-left transition-colors ${filter === "uncovered" ? "border-[#E24B4A]/60" : ""}`}
              data-testid="grc-kpi-uncovered">
              <div className="text-xl font-bold" style={{ color: (s?.uncovered_risks?.length || 0) > 0 ? "#E24B4A" : "#1D9E75" }}>
                {s?.uncovered_risks?.length ?? 0}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.axp.grc_kpi_uncovered")} <EpiBadge kind="DERIVED" /></div>
            </button>
            <button onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
              className={`noam-card px-4 py-3 text-left transition-colors ${filter === "overdue" ? "border-[#EF9F27]/60" : ""}`}
              data-testid="grc-kpi-overdue">
              <div className="text-xl font-bold" style={{ color: (s?.overdue_controls?.length || 0) > 0 ? "#EF9F27" : "#1D9E75" }}>
                {s?.overdue_controls?.length ?? 0}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.axp.grc_kpi_overdue")} <EpiBadge kind="DERIVED" /></div>
            </button>
            <button onClick={() => setFilter(filter === "below" ? "all" : "below")}
              className={`noam-card px-4 py-3 text-left transition-colors ${filter === "below" ? "border-[#E24B4A]/60" : ""}`}
              data-testid="grc-kpi-below">
              <div className="text-xl font-bold" style={{ color: (s?.below_threshold_controls?.length || 0) > 0 ? "#E24B4A" : "#1D9E75" }}>
                {s?.below_threshold_controls?.length ?? 0}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t("saas.axp.grc_kpi_below")} <EpiBadge kind="DERIVED" /></div>
            </button>
            <div className="noam-card px-4 py-3" data-testid="grc-kpi-levels">
              <div className="flex items-center gap-2">
                {Object.entries(s?.levels || {}).map(([lv, n]) => (
                  <button key={lv} onClick={() => setFilter(filter === lv ? "all" : lv)}
                    className="text-sm font-bold px-1.5 py-0.5 rounded transition-colors"
                    style={{ color: LEVEL_COLOR[lv] || "#a1a1aa", background: filter === lv ? `${LEVEL_COLOR[lv]}22` : "transparent" }}>
                    {n} {t(`saas.axp3.lvl_${lv}`, { defaultValue: lv })}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{t("saas.axp.grc_kpi_levels")}</div>
            </div>
          </div>

          {/* ─── Matrice P×I ─────────────────────────────────────────── */}
          <div className="grid lg:grid-cols-5 gap-5 items-start">
            <div className="lg:col-span-2 noam-card p-4" data-testid="grc-heatmap">
              <div className="noam-overline mb-3">{t("saas.axp.grc_heatmap")}</div>
              <div className="grid grid-cols-[auto_repeat(5,1fr)] gap-1">
                {[5, 4, 3, 2, 1].map((p) => (
                  <>
                    <div key={`p${p}`} className="text-[9px] text-zinc-500 flex items-center justify-center pr-1">P{p}</div>
                    {[1, 2, 3, 4, 5].map((i) => {
                      const items = cell(p, i);
                      const crit = p * i;
                      const bg = crit >= 15 ? "#E24B4A" : crit >= 8 ? "#EF9F27" : "#1D9E75";
                      return (
                        <div key={`${p}-${i}`} className="aspect-square rounded-md flex flex-wrap items-center justify-center gap-0.5 p-0.5"
                          style={{ background: `${bg}18`, border: `1px solid ${bg}30` }}>
                          {items.map((r) => (
                            <button key={r.id} onClick={() => openRisk(r)} title={`${r.code} — ${r.title}`}
                              data-testid={`grc-cell-risk-${r.code}`}
                              className="text-[8px] font-mono font-bold px-1 py-0.5 rounded-full text-black hover:scale-110 transition-transform"
                              style={{ background: LEVEL_COLOR[r.level] || "#71717a" }}>
                              {r.code || "•"}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </>
                ))}
                <div />
                {[1, 2, 3, 4, 5].map((i) => <div key={`i${i}`} className="text-[9px] text-zinc-500 text-center pt-1">I{i}</div>)}
              </div>
            </div>

            {/* ─── Liste par criticité ───────────────────────────────── */}
            <div className="lg:col-span-3 noam-card p-4" data-testid="grc-risk-list">
              <div className="noam-overline mb-3 flex items-center justify-between">
                <span>{t("saas.axp.grc_list", { n: filtered.length })}</span>
                {filter !== "all" && (
                  <button onClick={() => setFilter("all")} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1">
                    <Filter className="w-3 h-3" /> {t("saas.axp.grc_clear_filter")}
                  </button>
                )}
              </div>
              <div className="space-y-1.5 max-h-[430px] overflow-y-auto pr-1">
                {filtered.map((r) => (
                  <button key={r.id} onClick={() => openRisk(r)}
                    data-testid={`grc-risk-${r.code || r.id}`}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/20 transition-colors">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LEVEL_COLOR[r.level] || "#71717a" }} />
                    <span className="text-[10px] font-mono text-zinc-500 shrink-0">{r.code}</span>
                    <span className="text-xs text-zinc-200 flex-1 truncate">{r.title}</span>
                    {!r.covered && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E24B4A]/15 text-[#E24B4A] border border-[#E24B4A]/30 shrink-0">{t("saas.axp.grc_not_covered")}</span>}
                    {r.controls.some((c) => c.days_overdue) && <Clock className="w-3 h-3 text-[#EF9F27] shrink-0" />}
                    <span className="text-[10px] font-mono text-zinc-400 shrink-0">{r.criticality}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Navigation contextuelle : Matrice → Risque → Contrôle → Exécution → Provenance ── */}
      <ContextStack
        stack={stack}
        rootLabel={t("saas.axp.grc_root")}
        push={push}
        onPop={(n) => setStack((st) => st.slice(0, n))}
        onClose={() => setStack([])}
      />
    </div>
  );
}
